import { FilterCondition, TableHeaderType } from "../types";
import { useDatatableContext } from "../contexts/DatatableContextProvider";
import useFiltersBySearchParams from "../../../helpers/useFiltersBySearchParams";
import { useMemo } from "react";
import { useAppState } from "../../../modules/app/useAppContext";
import { SavedFilterUrlParam } from "../constants";

/**
 * Hook to get active filters from context and manage filter application
 * Provides both count and filter conditions for reuse across components
 */
const useActiveFilters = (headers?: TableHeaderType[]) => {
  const { getFilterValue, state } = useDatatableContext();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const { datastores } = useAppState();

  // Create datastore name to ID mapping for instance translation
  const datastoreNameToIdMap = useMemo(() => {
    return (datastores ?? []).reduce(
      (acc, datastore) => {
        acc[datastore.name] = datastore.instance_id;
        return acc;
      },
      {} as Record<string, string | null | undefined>
    );
  }, [datastores]);

  const getActiveFiltersAsConditions = (): FilterCondition[] => {
    const contextFilters: FilterCondition[] = [];

    // Get filters from headers
    if (headers) {
      headers.forEach(header => {
        if (header.filter?.searchKey) {
          const value = getFilterValue(header.filter.searchKey);
          if (value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0)) {
            contextFilters.push({
              field: header.filter.searchKey,
              value: value as string | string[],
              logicalOperator: contextFilters.length > 0 ? "AND" : undefined,
            });
          }
        }
      });
    }

    // Get search keys already added from headers
    const headerSearchKeys = new Set(
      headers?.filter(h => h.filter?.searchKey).map(h => h.filter!.searchKey!) ?? []
    );

    // Add programmatic filters from context state that aren't in headers
    Object.values(state.filters).forEach(filter => {
      if (filter.filterConfig?.searchKey && !headerSearchKeys.has(filter.filterConfig.searchKey)) {
        const value = getFilterValue(filter.filterConfig.searchKey);
        if (value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0)) {
          contextFilters.push({
            field: filter.filterConfig.searchKey,
            value: value as string | string[],
            logicalOperator: contextFilters.length > 0 ? "AND" : undefined,
          });
        }
      }
    });

    return contextFilters;
  };

  const count = useMemo(() => {
    let count = 0;

    // Count filters from headers
    if (headers) {
      headers.forEach(header => {
        if (header.filter?.searchKey) {
          const value = getFilterValue(header.filter.searchKey);
          if (value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0)) {
            count++;
          }
        }
      });
    }

    // Get search keys already counted from headers
    const headerSearchKeys = new Set(
      headers?.filter(h => h.filter?.searchKey).map(h => h.filter!.searchKey!) ?? []
    );

    // Count programmatic filters from context state that aren't in headers
    Object.values(state.filters).forEach(filter => {
      if (filter.filterConfig?.searchKey && !headerSearchKeys.has(filter.filterConfig.searchKey)) {
        const value = getFilterValue(filter.filterConfig.searchKey);
        if (value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0)) {
          count++;
        }
      }
    });

    return count;
  }, [getFilterValue, headers, state.filters]);

  /**
   * Unified method to apply filters to URL params
   * Handles instance name to ID translation
   */
  const applyFilters = (
    filters: Record<string, { values: FilterCondition["value"] }>,
    filterId?: string
  ): void => {
    const searchParamsUpdate: { [searchKey: string]: string | string[] | undefined } = {
      // reset page if applying filter
      page: "0",
    };

    // First, clear all existing filter params from headers
    if (headers) {
      headers.forEach(header => {
        if (header.filter?.searchKey) {
          // Set to undefined to remove from URL
          searchParamsUpdate[header.filter.searchKey] = undefined;
        }
      });
    }

    // Get search keys already covered by headers
    const headerSearchKeys = new Set(
      headers?.filter(h => h.filter?.searchKey).map(h => h.filter!.searchKey!) ?? []
    );

    // Also clear programmatic filter params (e.g., custom_tag_*) that aren't in headers
    Object.values(state.filters).forEach(filter => {
      if (filter.filterConfig?.searchKey && !headerSearchKeys.has(filter.filterConfig.searchKey)) {
        searchParamsUpdate[filter.filterConfig.searchKey] = undefined;
      }
    });

    // Then set the saved filter ID if provided
    if (filterId) {
      searchParamsUpdate[SavedFilterUrlParam] = filterId;
    } else {
      // Clear saved filter param if no filterId
      searchParamsUpdate[SavedFilterUrlParam] = undefined;
    }

    // Finally, apply the new filter values
    Object.entries(filters).forEach(([key, v]) => {
      const value = v.values;
      // Special handling for instance field - translate names to IDs
      if (key === "instance") {
        if (Array.isArray(value)) {
          const translated = value
            // default to actual value to preserve the id if passed
            .map(v => datastoreNameToIdMap[v as string] || v)
            .filter((v): v is string => typeof v === "string");
          if (translated.length > 0) {
            searchParamsUpdate[key] = translated;
          }
        } else if (typeof value === "string") {
          // default to actual value to preserve the id if passed
          const translated = datastoreNameToIdMap[value] || value;
          if (translated) {
            searchParamsUpdate[key] = translated;
          }
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        "value" in value &&
        "mode" in value
      ) {
        const filterFromHeader = headers?.find(h => h.filter?.searchKey === key)?.filter;
        if (filterFromHeader) {
          searchParamsUpdate[key] = value.value;
          if ("filterModeSearchKey" in filterFromHeader && filterFromHeader.filterModeSearchKey) {
            searchParamsUpdate[filterFromHeader.filterModeSearchKey] = value.mode;
          }
        }
      } else {
        // For other fields, just use the value directly
        searchParamsUpdate[key] = value;
      }
    });

    // Update URL search parameters (this will trigger context sync automatically)
    updateSearchParamsByKey(searchParamsUpdate);
  };

  return {
    activeFiltersCount: count,
    getActiveFiltersAsConditions,
    applyFilters,
  };
};

export default useActiveFilters;
